/**
 * Time handling.
 *
 * Instants are stored as UTC ISO-8601 strings. Everything a person sees --
 * "today", "this month", the budget period -- is bucketed in the user's zone,
 * which defaults to Asia/Kuala_Lumpur. Bucketing goes through Intl so it stays
 * correct for any zone the user might pick, not only the fixed +08:00 offset.
 */

export const DEFAULT_TIME_ZONE = 'Asia/Kuala_Lumpur';

export interface ZonedParts {
  year: number;
  month: number; // 1-12
  day: number; // 1-31
  hour: number;
  minute: number;
  second: number;
}

const partsFormatterCache = new Map<string, Intl.DateTimeFormat>();

function partsFormatter(timeZone: string): Intl.DateTimeFormat {
  let formatter = partsFormatterCache.get(timeZone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-GB', {
      timeZone,
      hour12: false,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    partsFormatterCache.set(timeZone, formatter);
  }
  return formatter;
}

export function toDate(value: Date | string | number): Date {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) throw new RangeError(`Invalid date: ${String(value)}`);
  return date;
}

/** Break an instant into wall-clock parts inside `timeZone`. */
export function zonedParts(value: Date | string | number, timeZone = DEFAULT_TIME_ZONE): ZonedParts {
  const date = toDate(value);
  const parts = partsFormatter(timeZone).formatToParts(date);
  const lookup: Record<string, number> = {};
  for (const part of parts) {
    if (part.type !== 'literal') lookup[part.type] = Number(part.value);
  }
  return {
    year: lookup.year ?? 0,
    month: lookup.month ?? 1,
    day: lookup.day ?? 1,
    // Some engines render midnight as hour 24 when hour12 is false.
    hour: (lookup.hour ?? 0) % 24,
    minute: lookup.minute ?? 0,
    second: lookup.second ?? 0,
  };
}

const pad = (value: number, width = 2): string => String(value).padStart(width, '0');

/** "YYYY-MM-DD" for the instant, in the given zone. */
export function dayKey(value: Date | string | number, timeZone = DEFAULT_TIME_ZONE): string {
  const parts = zonedParts(value, timeZone);
  return `${pad(parts.year, 4)}-${pad(parts.month)}-${pad(parts.day)}`;
}

/** "YYYY-MM" for the instant, in the given zone. */
export function monthKey(value: Date | string | number, timeZone = DEFAULT_TIME_ZONE): string {
  return dayKey(value, timeZone).slice(0, 7);
}

/** Offset of `timeZone` from UTC, in minutes, at the given instant. */
export function zoneOffsetMinutes(
  value: Date | string | number,
  timeZone = DEFAULT_TIME_ZONE,
): number {
  const date = toDate(value);
  const parts = zonedParts(date, timeZone);
  const wallAsUtc = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second,
  );
  const flooredToSecond = Math.floor(date.getTime() / 1000) * 1000;
  return Math.round((wallAsUtc - flooredToSecond) / 60000);
}

/**
 * Convert a wall-clock time in `timeZone` to the matching UTC instant.
 * Resolved iteratively so it stays correct across a daylight-saving boundary
 * (Malaysia has none, but the zone is a user setting).
 */
export function zonedTimeToUtc(
  parts: Partial<ZonedParts> & Pick<ZonedParts, 'year' | 'month' | 'day'>,
  timeZone = DEFAULT_TIME_ZONE,
): Date {
  const naive = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour ?? 0,
    parts.minute ?? 0,
    parts.second ?? 0,
  );
  let utc = naive;
  for (let pass = 0; pass < 3; pass += 1) {
    const rendered = zonedParts(new Date(utc), timeZone);
    const renderedAsUtc = Date.UTC(
      rendered.year,
      rendered.month - 1,
      rendered.day,
      rendered.hour,
      rendered.minute,
      rendered.second,
    );
    const drift = renderedAsUtc - naive;
    if (drift === 0) break;
    utc -= drift;
  }
  return new Date(utc);
}

/** Parse a "YYYY-MM-DD" day key. */
export function parseDayKey(key: string): { year: number; month: number; day: number } {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(key);
  if (!match) throw new RangeError(`Invalid day key: ${key}`);
  return { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) };
}

/** Parse a "YYYY-MM" month key. */
export function parseMonthKey(key: string): { year: number; month: number } {
  const match = /^(\d{4})-(\d{2})$/.exec(key);
  if (!match) throw new RangeError(`Invalid month key: ${key}`);
  return { year: Number(match[1]), month: Number(match[2]) };
}

export interface InstantRange {
  /** Inclusive lower bound, UTC ISO string. */
  startIso: string;
  /** Exclusive upper bound, UTC ISO string. */
  endIso: string;
}

/** The UTC range covering one local calendar day. */
export function dayRange(key: string, timeZone = DEFAULT_TIME_ZONE): InstantRange {
  const { year, month, day } = parseDayKey(key);
  const start = zonedTimeToUtc({ year, month, day }, timeZone);
  const next = new Date(Date.UTC(year, month - 1, day + 1));
  const end = zonedTimeToUtc(
    { year: next.getUTCFullYear(), month: next.getUTCMonth() + 1, day: next.getUTCDate() },
    timeZone,
  );
  return { startIso: start.toISOString(), endIso: end.toISOString() };
}

/** The UTC range covering one local calendar month. */
export function monthRange(key: string, timeZone = DEFAULT_TIME_ZONE): InstantRange {
  const { year, month } = parseMonthKey(key);
  const start = zonedTimeToUtc({ year, month, day: 1 }, timeZone);
  const next = month === 12 ? { year: year + 1, month: 1 } : { year, month: month + 1 };
  const end = zonedTimeToUtc({ ...next, day: 1 }, timeZone);
  return { startIso: start.toISOString(), endIso: end.toISOString() };
}

/** ISO weekday in the zone: 1 = Monday ... 7 = Sunday. */
export function isoWeekday(value: Date | string | number, timeZone = DEFAULT_TIME_ZONE): number {
  const { year, month, day } = zonedParts(value, timeZone);
  const weekday = new Date(Date.UTC(year, month - 1, day)).getUTCDay();
  return weekday === 0 ? 7 : weekday;
}

/** The UTC range covering the local Monday-start week containing `value`. */
export function weekRange(
  value: Date | string | number,
  timeZone = DEFAULT_TIME_ZONE,
): InstantRange {
  const parts = zonedParts(value, timeZone);
  const weekday = isoWeekday(value, timeZone);
  const monday = new Date(Date.UTC(parts.year, parts.month - 1, parts.day - (weekday - 1)));
  const start = zonedTimeToUtc(
    { year: monday.getUTCFullYear(), month: monday.getUTCMonth() + 1, day: monday.getUTCDate() },
    timeZone,
  );
  const nextMonday = new Date(monday.getTime() + 7 * 86400000);
  const end = zonedTimeToUtc(
    {
      year: nextMonday.getUTCFullYear(),
      month: nextMonday.getUTCMonth() + 1,
      day: nextMonday.getUTCDate(),
    },
    timeZone,
  );
  return { startIso: start.toISOString(), endIso: end.toISOString() };
}

/** Add days to a day key, staying in calendar space. */
export function addDays(key: string, days: number): string {
  const { year, month, day } = parseDayKey(key);
  const shifted = new Date(Date.UTC(year, month - 1, day + days));
  return `${pad(shifted.getUTCFullYear(), 4)}-${pad(shifted.getUTCMonth() + 1)}-${pad(
    shifted.getUTCDate(),
  )}`;
}

/** Add months to a month key. */
export function addMonths(key: string, months: number): string {
  const { year, month } = parseMonthKey(key);
  const shifted = new Date(Date.UTC(year, month - 1 + months, 1));
  return `${pad(shifted.getUTCFullYear(), 4)}-${pad(shifted.getUTCMonth() + 1)}`;
}

/** Number of days in the local month. */
export function daysInMonth(key: string): number {
  const { year, month } = parseMonthKey(key);
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/** Whether an ISO instant falls inside a half-open range. */
export function isWithin(iso: string, range: InstantRange): boolean {
  return iso >= range.startIso && iso < range.endIso;
}

/** Local "YYYY-MM-DDTHH:mm", the shape an `<input type="datetime-local">` wants. */
export function toLocalInputValue(
  value: Date | string | number,
  timeZone = DEFAULT_TIME_ZONE,
): string {
  const parts = zonedParts(value, timeZone);
  return `${pad(parts.year, 4)}-${pad(parts.month)}-${pad(parts.day)}T${pad(parts.hour)}:${pad(
    parts.minute,
  )}`;
}

/** Inverse of {@link toLocalInputValue}. */
export function fromLocalInputValue(value: string, timeZone = DEFAULT_TIME_ZONE): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?$/.exec(value.trim());
  if (!match) throw new RangeError(`Invalid local date-time: ${value}`);
  return zonedTimeToUtc(
    {
      year: Number(match[1]),
      month: Number(match[2]),
      day: Number(match[3]),
      hour: Number(match[4]),
      minute: Number(match[5]),
      second: Number(match[6] ?? 0),
    },
    timeZone,
  ).toISOString();
}

const labelCache = new Map<string, Intl.DateTimeFormat>();

function labelFormatter(timeZone: string, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = `${timeZone}:${JSON.stringify(options)}`;
  let formatter = labelCache.get(key);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-GB', { timeZone, ...options });
    labelCache.set(key, formatter);
  }
  return formatter;
}

/** "Mon, 17 May 2024" */
export function formatDayLabel(value: Date | string | number, timeZone = DEFAULT_TIME_ZONE): string {
  return labelFormatter(timeZone, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(toDate(value));
}

/** "8:42 pm" */
export function formatTimeLabel(
  value: Date | string | number,
  timeZone = DEFAULT_TIME_ZONE,
): string {
  return labelFormatter(timeZone, { hour: 'numeric', minute: '2-digit', hour12: true })
    .format(toDate(value))
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

/** "May 2024" for a "YYYY-MM" key. */
export function formatMonthLabel(key: string, timeZone = DEFAULT_TIME_ZONE): string {
  const range = monthRange(key, timeZone);
  return labelFormatter(timeZone, { month: 'long', year: 'numeric' }).format(
    new Date(range.startIso),
  );
}

/** "Today", "Yesterday", otherwise the full day label. */
export function formatRelativeDay(
  value: Date | string | number,
  now: Date | string | number = new Date(),
  timeZone = DEFAULT_TIME_ZONE,
): string {
  const target = dayKey(value, timeZone);
  const today = dayKey(now, timeZone);
  if (target === today) return 'Today';
  if (target === addDays(today, -1)) return 'Yesterday';
  return formatDayLabel(value, timeZone);
}

/** Round an instant down to the minute -- used for deduplication keys. */
export function truncateToMinute(value: Date | string | number): string {
  const date = toDate(value);
  return new Date(Math.floor(date.getTime() / 60000) * 60000).toISOString();
}
